import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authApi } from '@/services/api';
import {
  authPageStyles,
  authTextStyles,
  authAlertStyles,
  authVisualStyles,
  authButtonStyles,
} from '@/styles/authPageStyles';
import { CheckademicLogo } from '@/components/CheckademicLogo';
import { SchoolLogo } from '@/components/SchoolLogo';
import { LanguageToggle } from '@/components/parent/LanguageToggle';
import { useAuthStore } from '@/stores/authStore';
import { usePageTitle } from '@/hooks/usePageTitle';

export default function VerifyEmail() {
  const { t } = useTranslation('common');
  usePageTitle(t('auth.verifyEmail.pageTitle'));
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() ?? null;

  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { tenant, logout, initializeAuth } = useAuthStore();

  const hasVerified = useRef(false);

  useEffect(() => {
    if (hasVerified.current) return;
    hasVerified.current = true;

    const verify = async () => {
      if (!token) {
        setError(t('auth.verifyEmail.noToken'));
        setVerifying(false);
        return;
      }

      try {
        const response = await authApi.verifyEmail(token);

        if (response.success) {
          setSuccess(true);
          setEmailChanged(!!response.emailChanged);
          if (response.emailChanged) {
            // Log out stale session so user must log in with new email
            logout();
          } else {
            // Refresh auth state so Profile page badge updates
            initializeAuth();
          }
        } else {
          setError(response.message || t('auth.verifyEmail.verificationFailed'));
        }
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : t('auth.verifyEmail.invalidOrExpired');
        setError(errorMessage);
      } finally {
        setVerifying(false);
      }
    };

    verify();
  }, [token, initializeAuth, logout, t]);

  const logoSection = (
    <>
      {tenant?.settings.branding.logoFileId && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
          <SchoolLogo logoFileId={tenant.settings.branding.logoFileId} schoolName={tenant.schoolName} size="md" />
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
        <CheckademicLogo
          primaryColor="var(--primary-color)"
          accentColor="var(--secondary-color)"
          textColor="var(--primary-color)"
        />
      </div>
    </>
  );

  // Verifying token
  if (verifying) {
    return (
      <div style={authPageStyles.pageContainer}>
        <div style={authPageStyles.cardContainerCentered}>
          {logoSection}
          <div style={authVisualStyles.loadingEmoji}>
            <Loader2 size={48} aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>{t('auth.verifyEmail.verifying')}</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div style={authPageStyles.pageContainer}>
        <div style={authPageStyles.cardContainerCentered}>
          <LanguageToggle />
          {logoSection}
          <h1 style={authTextStyles.titleCentered}>{t('auth.verifyEmail.failedTitle')}</h1>
          <div style={authAlertStyles.error}>{error}</div>
          <p style={authTextStyles.description}>
            {t('auth.verifyEmail.failedDescription')}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/login')}
            style={authButtonStyles.fullWidth}
          >
            {t('auth.verifyEmail.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  // Success — email change
  if (success && emailChanged) {
    return (
      <div style={authPageStyles.pageContainer}>
        <div style={authPageStyles.cardContainerCentered}>
          <LanguageToggle />
          {logoSection}
          <h1 style={authTextStyles.titleCentered}>{t('auth.verifyEmail.emailChangedTitle')}</h1>
          <div style={authAlertStyles.success}>{t('auth.verifyEmail.emailChangedSuccess')}</div>
          <p style={authTextStyles.description}>
            {t('auth.verifyEmail.emailChangedInstruction')}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/login')}
            style={authButtonStyles.fullWidth}
          >
            {t('auth.verifyEmail.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  // Success — registration verification
  return (
    <div style={authPageStyles.pageContainer}>
      <div style={authPageStyles.cardContainerCentered}>
        <LanguageToggle />
        {logoSection}
        <h1 style={authTextStyles.titleCentered}>{t('auth.verifyEmail.verifiedTitle')}</h1>
        <div style={authAlertStyles.success}>
          {t('auth.verifyEmail.verifiedSuccess')}
        </div>
        <p style={authTextStyles.description}>
          {t('auth.verifyEmail.verifiedInstruction')}
        </p>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/')}
          style={authButtonStyles.fullWidth}
        >
          {t('auth.verifyEmail.goToDashboard')}
        </button>
      </div>
    </div>
  );
}
